/**
 * SecretsExportPage — admin self-serve master credentials download.
 *
 * Flow (founder ruling 30 Jun 2026):
 *   1. Admin lands here from /admin/secrets/export.
 *   2. Re-enters their password (fresh verification on top of existing
 *      admin session · catches "left laptop open").
 *   3. POST /api/admin/secrets/export/request mints a one-time token.
 *   4. Browser immediately fetches GET /api/admin/secrets/export/{token}
 *      which streams the .docx and burns the token. No disk artefact.
 *   5. The download starts. The token cannot be reused.
 *
 * The X-Admin-Secret header is set the same way as the rest of the
 * admin console (read from sessionStorage).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

function adminSecret() {
  // Same key used by AdminLogin elsewhere in the console.
  return (typeof window !== "undefined" && window.sessionStorage)
    ? (sessionStorage.getItem("lc_admin_secret") || "")
    : "";
}

export default function SecretsExportPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password.trim()) {
      setError("Re-enter your password to continue.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/secrets/export/request`, {
        method:  "POST",
        headers: {
          "Content-Type":    "application/json",
          "X-Admin-Secret":  adminSecret(),
        },
        body: JSON.stringify({ password: password.trim() }),
      });
      if (r.status === 401) throw new Error("Admin session expired. Sign in again.");
      if (r.status === 403) throw new Error("Password did not match. Try again.");
      if (!r.ok) throw new Error(`Server refused (${r.status}). Check logs.`);
      const j = await r.json();
      if (!j.token) throw new Error("No token returned. Try again.");
      // Trigger the single-use download by navigating to the token URL.
      // The browser handles the .docx as an attachment download · the
      // current tab stays put.
      window.location.href = `${API}/api/admin/secrets/export/${encodeURIComponent(j.token)}`;
      setSuccess(true);
      setPassword("");
    } catch (err) {
      setError(err.message || "Could not mint export token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-secrets-export-page"
      style={{
        minHeight:  "100vh",
        background: "#0F0F12",
        color:      "#E8E5DD",
        padding:    "64px 24px",
        fontFamily: "system-ui, -apple-system, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <button
          type="button"
          onClick={() => navigate("/admin")}
          data-testid="admin-secrets-back"
          style={{
            background: "transparent",
            border:     "1px solid #2C2A26",
            color:      "#8A8478",
            borderRadius: 8,
            padding:    "6px 14px",
            fontSize:   12,
            cursor:     "pointer",
            marginBottom: 32,
          }}
        >
          ← Back to admin console
        </button>

        <h1 style={{
          fontFamily:  "'Fraunces', 'EB Garamond', serif",
          fontSize:    32,
          fontWeight:  400,
          margin:      "0 0 8px",
          color:       "#F4EFE6",
          letterSpacing: "-0.01em",
        }}>
          Master Secrets · Self-Serve Export
        </h1>
        <p style={{ fontSize: 14, color: "#8A8478", margin: "0 0 32px", lineHeight: 1.6 }}>
          Every API key, password, webhook secret and credential in PROD,
          packaged into a single <code>.docx</code>. Treat the file like a
          banking PIN list. Re-enter your password to confirm — the
          download link is one-time-use and expires 5 minutes after mint.
        </p>

        <div style={{
          background:   "#1A1916",
          border:       "1px solid #2C2A26",
          borderRadius: 12,
          padding:      24,
          marginBottom: 24,
        }}>
          <p style={{
            fontSize:       11,
            color:          "#C9A86A",
            fontFamily:     "ui-monospace, monospace",
            letterSpacing:  "0.18em",
            textTransform:  "uppercase",
            fontWeight:     700,
            margin:         "0 0 14px",
          }}>
            What this generates
          </p>
          <ul style={{ fontSize: 13, color: "#C9C5BC", lineHeight: 1.7, margin: 0, paddingLeft: 20 }}>
            <li>Section 1 — Production admin login</li>
            <li>Section 2 — Full backend <code>.env</code> (every key + value)</li>
            <li>Section 3 — Frontend <code>.env</code></li>
            <li>Section 4 — Integration → provider console reference</li>
            <li>Section 5 — Rotation reminders</li>
          </ul>
        </div>

        <form onSubmit={onSubmit} data-testid="admin-secrets-form">
          <label style={{ display: "block", fontSize: 12, color: "#8A8478", marginBottom: 8 }}>
            Re-enter admin password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            data-testid="admin-secrets-password"
            placeholder="••••••••"
            style={{
              width:        "100%",
              background:   "#1A1916",
              border:       "1px solid #2C2A26",
              color:        "#F4EFE6",
              borderRadius: 10,
              padding:      "14px 16px",
              fontSize:     15,
              fontFamily:   "inherit",
              outline:      "none",
              marginBottom: 20,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#8B6914")}
            onBlur={(e) => (e.target.style.borderColor = "#2C2A26")}
          />

          {error && (
            <p
              data-testid="admin-secrets-error"
              style={{ fontSize: 12, color: "#E55B3C", margin: "0 0 16px", lineHeight: 1.5 }}
            >
              {error}
            </p>
          )}

          {success && (
            <p
              data-testid="admin-secrets-success"
              style={{ fontSize: 12, color: "#8B6914", margin: "0 0 16px", lineHeight: 1.5 }}
            >
              ✓ Token minted. Download started. The link is now invalidated · come back here any time.
            </p>
          )}

          <button
            type="submit"
            disabled={busy || !password.trim()}
            data-testid="admin-secrets-submit"
            style={{
              width:        "100%",
              background:   busy || !password.trim() ? "#3A372F" : "#8B6914",
              color:        "#F4EFE6",
              border:       0,
              borderRadius: 10,
              padding:      "14px 16px",
              fontSize:     14,
              fontWeight:   600,
              cursor:       busy || !password.trim() ? "not-allowed" : "pointer",
              fontFamily:   "inherit",
              letterSpacing: "0.02em",
            }}
          >
            {busy ? "Minting one-time link…" : "Verify & download secrets"}
          </button>
        </form>

        <p style={{ fontSize: 11, color: "#5C5447", margin: "32px 0 0", lineHeight: 1.6, textAlign: "center" }}>
          Each export is recorded in the audit trail at <code>/app/memory/secrets_archive/</code>
          with a timestamp + SHA-256. The webroot stays clean.
        </p>
      </div>
    </div>
  );
}
